import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import {
    Dashboard as DashboardIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Calculate as CalculateIcon,
    Receipt as ReceiptIcon,
    Assessment as AssessmentIcon,
    Settings as SettingsIcon,
    AccountBalance as AccountBalanceIcon,
    Paid as PaidIcon,
    RequestQuote as RequestQuoteIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import HRPayrollDashboard from "./hrPayrollDashboard";
import HRCompensationTab from "./hrCompensationTab";
import ITDeclarationsTab from "./ITDeclarationsTab";
import ReimbursementsTab from "./reimbursementsTab";
import RunPayroll from "./runPayrollTab/runPayrollTab";
import PayrollRecordsTab from "./payrollRecordsTab";
import TransactionDetailsPanel from "./transactionDetailsTab";
import PayslipDownloadTab from "./payslipDownloadTab";
import PayslipReportsTab from "./payslipReportsTab";
import PayrollSettings from "./payrollSettingsTab";
import MyPayrollOverview from "./myPayrollOverview";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

const StyledTabs = styled(Tabs)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    padding: '6px',
    width: '100%',
    maxWidth: '100%',
    minHeight: 48,
    '& .MuiTabs-indicator': {
        height: 0,
    },
    '& .MuiTabs-scroller': {
        overflow: 'auto !important',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    '& .MuiTabs-flexContainer': {
        display: 'flex',
    },
    '& .MuiTab-root': {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        textTransform: 'none',
        minHeight: 48,
        borderRadius: 8,
        margin: '0 4px',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
}));

function HRPayrollManagement({ setSelectedMenu }) {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;

    const [payrollSubtype, setPayrollSubtype] = useState("company");
    const [activeTab, setActiveTab] = useState(0);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    // Sync activeTab whenever user clicks a tab
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Fetch Employees
    useEffect(() => {
        const fetchEmployees = async () => {
            if (!orgId) return;
            try {
                const response = await getData(API_ENDPOINTS.fetchEmployeesByOrganizationId(orgId));
                setEmployees(response.message.employees || []);
            } catch (error) {
                return null;
            }
        };
        fetchEmployees();
    }, [orgId]);

    // Handle External Tab Navigation
    useEffect(() => {
        const tabToOpen = localStorage.getItem("openPayrollTab");
        if (tabToOpen === "Compensation") setActiveTab(1);
        else if (tabToOpen === "IT Declarations") setActiveTab(2);
        else if (tabToOpen === "Run Payroll") setActiveTab(4);
        localStorage.removeItem("openPayrollTab");
    }, []);

    // Logic Handlers
    const handleNavigateToCompensation = (empId) => {
        setSelectedEmployeeId(empId);
        setActiveTab(1);
    };

    const handleBackFromITDeclarations = () => {
        localStorage.setItem("returnToRunPayrollStep", "2");
        localStorage.removeItem("returnToRunPayrollFromITDeclarations");
        setActiveTab(4);
    };

    const handleNavigateToEmployeeBanking = (empId) => {
        localStorage.setItem("openEmployeeManagementEmployee", JSON.stringify({
            employeeId: empId,
            tab: 3,
            source: "runPayroll",
            returnStep: 5,
        }));
        setSelectedMenu("Employee Management");
    };

    const handleNavigateToEmployeeCompensationPay = (empId) => {
        localStorage.setItem("openEmployeeManagementEmployee", JSON.stringify({
            employeeId: empId,
            tab: 2,
            source: "runPayrollCompensation",
            returnPayrollTab: "Compensation",
        }));
        navigate(`/hr/employee-details?employeeId=${empId}`);
    };

    useEffect(() => {
        const tabToOpen = localStorage.getItem("openPayrollTab");
        if (tabToOpen === "Compensation") {
            setActiveTab(1);
        } else if (tabToOpen === "Run Payroll") {
            setActiveTab(4);
        } else if (tabToOpen === "IT Declarations") {
            setActiveTab(2);
        }

        localStorage.removeItem("openPayrollTab");
    }, []);

    return (
        <Box sx={{
            p: 1,
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            boxSizing: 'border-box'
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                    Payroll
                </Typography>
                <Tabs
                    value={payrollSubtype}
                    onChange={(e, val) => setPayrollSubtype(val)}
                >
                    <Tab value="company" label="Company Payroll" />
                    <Tab value="my" label="My Payroll" />
                </Tabs>
            </Box>

            {payrollSubtype === "company" ? (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '100%',
                    width: '100%',
                    minWidth: 0
                }}>
                    <StyledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="payroll management tabs"
                    >
                        <Tab icon={<DashboardIcon fontSize="small" />} label="Dashboard" />
                        <Tab icon={<PaidIcon fontSize="small" />} label="Compensation" />
                        <Tab icon={<CalculateIcon fontSize="small" />} label="IT Declarations" />
                        <Tab icon={<RequestQuoteIcon fontSize="small" />} label="Reimbursements" />
                        <Tab icon={<PlayCircleOutlineIcon fontSize="small" />} label="Run Payroll" />
                        <Tab icon={<PaidIcon fontSize="small" />} label="Records" />
                        <Tab icon={<AccountBalanceIcon fontSize="small" />} label="Transactions" />
                        <Tab icon={<ReceiptIcon fontSize="small" />} label="Payslips" />
                        <Tab icon={<AssessmentIcon fontSize="small" />} label="Reports" />
                        <Tab icon={<SettingsIcon fontSize="small" />} label="Settings" />
                    </StyledTabs>

                    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
                        {activeTab === 0 && <HRPayrollDashboard employees={employees} />}

                        {activeTab === 1 && (
                            <HRCompensationTab
                                selectedEmployeeId={selectedEmployeeId}
                                onPayrollSettingsMissing={handleNavigateToEmployeeCompensationPay}
                                onBack={() => {
                                    setActiveTab(4);
                                    localStorage.setItem("returnToRunPayrollStep", "0");
                                    setSelectedEmployeeId(null);
                                }}
                            />
                        )}

                        {activeTab === 2 && (
                            <ITDeclarationsTab
                                employees={employees}
                                onBack={handleBackFromITDeclarations}
                            />
                        )}

                        {activeTab === 3 && <ReimbursementsTab employees={employees} />}

                        {activeTab === 4 && (
                            <RunPayroll
                                employees={employees}
                                onNavigateToCompensation={handleNavigateToCompensation}
                                onNavigateToEmployeeBanking={handleNavigateToEmployeeBanking}
                                onNavigateToPayrollRecords={() => setActiveTab(5)}
                                setCompensationConfirmed={() => { }}
                                setLeaveAttendanceConfirmed={() => { }}
                                setITDeclarationsConfirmed={() => { }}
                                setLeaveEncashConfirmed={() => { }}
                                setDetailsConfirmed={() => { }}
                            />
                        )}

                        {activeTab === 5 && (
                            <PayrollRecordsTab
                                employees={employees}
                                onNavigateToRunPayrollProcess={() => {
                                    localStorage.setItem("openRunPayrollProcessOverview", "1");
                                    setActiveTab(4);
                                }}
                            />
                        )}

                        {activeTab === 6 && <TransactionDetailsPanel />}
                        {activeTab === 7 && <PayslipDownloadTab employees={employees} />}
                        {activeTab === 8 && <PayslipReportsTab employees={employees} />}
                        {activeTab === 9 && <PayrollSettings />}
                    </Box>
                </Box>
            ) : (
                <MyPayrollOverview />
            )}
        </Box>
    );
}

export default HRPayrollManagement;